/**
 * useTaskPauses - Hook para gerenciamento de pausas em tarefas
 * 
 * Gerencia query de histórico de pausas (get_task_pauses) e
 * mutations para pausar (pause_task) e retomar (resume_task).
 * 
 * @version 1.0.0
 * RELEASE: 2026-04-11 v1.0.0 - Criação inicial
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

/** Logger do módulo */
const logger = createLogger('useTaskPauses');

/**
 * Item de pausa retornado pela RPC get_task_pauses
 */
export interface TaskPauseItem {
  id: string;
  task_id: string;
  paused_by: string;
  paused_by_name: string | null;
  paused_by_avatar: string | null;
  paused_at: string;
  resumed_by: string | null;
  resumed_by_name: string | null;
  resumed_by_avatar: string | null;
  resumed_at: string | null;
  reason: string | null;
  duration_seconds: number | null;
}

/**
 * Resultado completo da RPC get_task_pauses
 */
export interface TaskPausesResult {
  pauses: TaskPauseItem[];
  total_count: number;
  total_duration_seconds: number;
}

/**
 * Hook para gerenciamento de pausas em tarefas
 * 
 * @param taskId - ID da tarefa (null para desabilitar)
 * @param enabled - Se a query deve ser habilitada (ex: aba ativa)
 */
export function useTaskPauses(taskId: string | null, enabled = true) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  /** Query para buscar histórico de pausas */
  const pausesQuery = useQuery({
    queryKey: ['task-pauses', taskId],
    queryFn: async (): Promise<TaskPausesResult> => {
      const { data, error } = await supabase.rpc('get_task_pauses', {
        p_task_id: taskId!,
        p_tenant_hint: currentTenant?.id || null,
      });

      if (error) {
        logger.error('Erro ao buscar pausas da tarefa', { error: error.message });
        throw error;
      }

      const items = (data || []) as TaskPauseItem[];

      // Calcula totais a partir da lista
      const totalDuration = items.reduce((acc, p) => acc + (p.duration_seconds || 0), 0);

      return {
        pauses: items,
        total_count: items.length,
        total_duration_seconds: totalDuration,
      };
    },
    enabled: !!taskId && !!currentTenant?.id && enabled,
  });

  /** Chaves de cache a invalidar após pausar/retomar */
  const invalidateRelated = () => {
    queryClient.invalidateQueries({ queryKey: ['task-details', taskId] });
    queryClient.invalidateQueries({ queryKey: ['task-pauses', taskId] });
    queryClient.invalidateQueries({ queryKey: ['task-kanban-column'] });
    queryClient.invalidateQueries({ queryKey: ['task-kanban-counts'] });
    queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
  };

  /** Mutation para pausar tarefa */
  const pauseMutation = useMutation({
    mutationFn: async ({ reason }: { reason?: string }) => {
      const { error } = await supabase.rpc('pause_task', {
        p_task_id: taskId!,
        p_reason: reason || null,
        p_tenant_hint: currentTenant?.id || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateRelated();
      toast.success('Tarefa pausada');
    },
    onError: (err: Error) => {
      logger.error('Erro ao pausar tarefa', { error: err.message });
      toast.error(`Erro ao pausar: ${err.message}`);
    },
  });

  /** Mutation para retomar tarefa */
  const resumeMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('resume_task', {
        p_task_id: taskId!,
        p_tenant_hint: currentTenant?.id || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateRelated();
      toast.success('Tarefa retomada');
    },
    onError: (err: Error) => {
      logger.error('Erro ao retomar tarefa', { error: err.message });
      toast.error(`Erro ao retomar: ${err.message}`);
    },
  });

  return {
    /** Lista de pausas */
    pauses: pausesQuery.data?.pauses || [],
    /** Total de pausas */
    totalCount: pausesQuery.data?.total_count || 0,
    /** Tempo total de pausa em segundos */
    totalDurationSeconds: pausesQuery.data?.total_duration_seconds || 0,
    /** Se está carregando */
    isLoading: pausesQuery.isLoading,
    /** Pausar tarefa (com motivo opcional) */
    pauseTask: pauseMutation.mutateAsync,
    /** Se está pausando */
    isPausing: pauseMutation.isPending,
    /** Retomar tarefa */
    resumeTask: resumeMutation.mutateAsync,
    /** Se está retomando */
    isResuming: resumeMutation.isPending,
  };
}
